import React, { useState } from 'react';
import {
  Box,
  TextField,
  IconButton,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import { observer, useLocalObservable } from 'mobx-react-lite';
import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import { PhotoCamera } from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import { action, runInAction } from 'mobx';
import { ExtendedVideo, Video, VideoWithImage } from '../../types/transport';
import Dropzone from '../../hooks/Dropzone';
import { processVideo, ProgressEntry } from '../../video';
import { addVideoFile } from '../../firebaseConnection/storage';
import LinearProgressWithLabel from '../LinearProgressWithLabel';

const AddVideoDialog = observer(
  ({
    video,
    category,
    onClose,
    onSave,
  }: {
    video: ExtendedVideo | undefined;
    category: string;
    onClose: () => void;
    onSave: (video: Video, image: File) => void;
  }): JSX.Element => {
    const handleClose = (): void => {
      onClose();
    };

    const onCloseBtn = (
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      reason: string,
    ): void => {
      if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') onClose();
    };
    const [videoFinished, setVideoFinished] = useState(false);

    const formData = useLocalObservable(() => ({
      title: video?.title || '',
      description: video?.description || '',
      url: video?.url || '',
      uuid: video?.uuid || uuidv4(),
      uploadDate: video?.uploadDate || Number(new Date()),
      image: new File([], ''),
      thumbnailUrl: video?.localThumbnailImageUrl || '',
      thumbnailFileName: video?.thumbnailUrl || '',
    }));

    const videoUpload = useLocalObservable(() => ({
      progress: { stage: 'transcoding', progress: 0 } as ProgressEntry,
      setProgress(progress: ProgressEntry) {
        this.progress = progress;
      },
    }));

    const captureFile: React.ChangeEventHandler<HTMLInputElement> = async ({
      target: input,
    }) => {
      runInAction(() => {
        if (input.files) {
          const [inputFile] = Array.from(input.files);
          formData.thumbnailUrl = URL.createObjectURL(inputFile);
          formData.thumbnailFileName = inputFile.name;
          formData.image = inputFile;
        }
      });
    };

    const onDrop = async (videoFile: File[]) => {
      // TODO: this is only an example
      const storageDirectory = uuidv4();
      setVideoFinished(false);
      try {
        const videoUrl = await processVideo(
          videoFile[0],
          (filename: string, fileData: Uint8Array) =>
            addVideoFile(storageDirectory, filename, fileData),
          // progress callback:
          (progress: ProgressEntry) => videoUpload.setProgress(progress),
        );
        setVideoFinished(true);
        console.log('video is here:', videoUrl);
        formData.url = videoUrl;
      } catch (e) {
        console.log(e);
      }
    };

    const saveEntry = () => {
      const videoData: VideoWithImage = {
        title: formData.title,
        description: formData.description,
        url: formData.url,
        category,
        uuid: formData.uuid,
        uploadDate: formData.uploadDate,
        thumbnailUrl: formData.thumbnailFileName,
        localThumbnailImageUrl: formData.thumbnailUrl,
      };

      onSave(videoData, formData.image);
    };

    window.onbeforeunload = event => {
      const e = event || window.event;
      if (e && videoUpload.progress.progress > 0) {
        e.returnValue = '';
      }
      return '';
    };

    return (
      <Dialog fullWidth maxWidth="md" open onClose={onCloseBtn}>
        <fieldset
          disabled={
            videoUpload.progress.progress > 0 &&
            videoUpload.progress.stage === 'transcoding'
          }
        >
          <DialogTitle>Video hinzufügen</DialogTitle>
          <Box sx={{ p: 2 }}>
            <TextField
              id="outlined-basic"
              label="Titel"
              variant="outlined"
              sx={{ m: 1 }}
              fullWidth
              value={formData.title}
              onChange={action(e => {
                formData.title = e.target.value;
              })}
            />
            <TextField
              id="outlined-basic"
              label="Beschreibung"
              variant="outlined"
              multiline
              rows={6}
              fullWidth
              sx={{ m: 1 }}
              value={formData.description}
              onChange={action(e => {
                formData.description = e.target.value;
              })}
            />
            <Box sx={{ marginTop: 2, marginBottom: 2 }}>
              <label htmlFor="uploadthumb">
                Video Thumbnail
                <input
                  accept="image/*"
                  id="uploadthumb"
                  type="file"
                  hidden
                  onChange={captureFile}
                />
                <IconButton
                  color="primary"
                  aria-label="upload picture"
                  component="span"
                >
                  <PhotoCamera />
                </IconButton>
                {formData.thumbnailUrl && (
                  <img
                    alt="Thumbnail"
                    src={formData.thumbnailUrl}
                    id="Thumbnail"
                    style={{
                      maxWidth: '300px',
                      display: 'block',
                      marginBottom: 20,
                    }}
                  />
                )}
              </label>
              <Typography variant="body1"> Video hochladen</Typography>
              {formData.url !== '' && (
                <Typography variant="body1">
                  {`Videourl: ${formData.url}`}
                </Typography>
              )}
              <Dropzone drop={csvFile => onDrop(csvFile)} />
              {!videoFinished &&
                videoUpload.progress.progress < 1 &&
                videoUpload.progress.progress > 0 && (
                  <LinearProgressWithLabel value={videoUpload.progress} />
                )}
            </Box>
          </Box>
        </fieldset>
        <DialogActions>
          <Button
            disabled={
              !videoFinished &&
              videoUpload.progress.progress < 1 &&
              videoUpload.progress.progress > 0
            }
            onClick={handleClose}
          >
            Abbrechen
          </Button>
          <Button
            disabled={
              !videoFinished &&
              videoUpload.progress.progress < 1 &&
              videoUpload.progress.progress > 0
            }
            onClick={saveEntry}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

export default AddVideoDialog;
